import AsyncAutocompleteInput from '@/components/form/AsyncAutocompleteInput'
import MessageForm, { MessageInternalNotesPanel } from '@/components/forms/MessageForm'
import {
  AdminDetailGrid,
  AdminHeaderActionButton,
  AdminHeaderIconButton,
  AdminRecordHeader,
  IconActionButton,
} from '@/components/patterns/admin-page'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  DetailCard,
  DetailIconBadge,
  DetailWorkspacePanel,
  DetailWorkspaceTab,
  DetailWorkspaceTabs,
  DetailWorkspaceTabsList,
  DetailWell,
  detailPaleIconClass,
  detailSurfaceActionClass,
} from '@/components/patterns/detail-page'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { Can, useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { CustomerSourceType } from '@/lib/enums/CustomerSourceType'
import { useAppForm } from '@/lib/form'
import { cn, getErrorMessage, normalizeEmailInput } from '@/lib/utils'
import { useCreateCustomerPosCartMutation } from '@/lib/services/customers/customers.query'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { getCustomers } from '@/lib/services/customers/customers.api'
import {
  messageQueries,
  useCreateMessageCustomerMutation,
  useLinkMessageCustomerMutation,
  useMarkMessageUnreadMutation,
  useMarkMessageReadMutation,
  useMessage,
  useSoftDeleteMessageMutation,
  useUpdateMessageMutation,
} from '@/lib/services/messages/messages.query'
import {
  messageContactInformationSchema,
  messageIdSchema,
} from '@/lib/services/messages/messages.schema'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import {
  ChevronDown,
  EllipsisVertical,
  Link2,
  Mail,
  MailCheck,
  MailOpen,
  MessageSquareText,
  NotebookPen,
  Pencil,
  Phone,
  Plus,
  Reply,
  ShoppingCart,
  Trash2,
  Unlink,
  UserPlus,
} from 'lucide-react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'

function getMessageSourceSummary(message) {
  const createdAtText = message?.created_at
    ? dayjs(message.created_at).format('MMM D, YYYY [at] h:mm A')
    : null

  const wasSubmittedOnline = Boolean(
    message?.source_page || message?.submitted_from_ip || message?.submitted_user_agent
  )

  const actionText = wasSubmittedOnline ? 'Submitted online' : 'Created in admin'

  return createdAtText ? `${actionText} on ${createdAtText}` : actionText
}

function getFirstName(name) {
  if (typeof name !== 'string') {
    return null
  }

  const [firstName] = name.trim().split(/\s+/)

  return firstName || null
}

function getReplySenderFirstName(name) {
  if (typeof name !== 'string' || /admin/i.test(name)) {
    return null
  }

  return getFirstName(name)
}

function normalizePhoneHref(phone) {
  if (typeof phone !== 'string') {
    return ''
  }

  const trimmedPhone = phone.trim()
  if (!trimmedPhone) {
    return ''
  }

  const hasLeadingPlus = trimmedPhone.startsWith('+')
  const digitsOnly = trimmedPhone.replace(/\D/g, '')

  if (!digitsOnly) {
    return ''
  }

  return hasLeadingPlus ? `+${digitsOnly}` : digitsOnly
}

function buildReplyDraft({ message, organizationName, userName }) {
  const customerFirstName = getFirstName(message?.name) || 'there'
  const senderFirstName = getReplySenderFirstName(userName)
  const floristName = organizationName || 'our flower shop'
  const submittedOn = message?.created_at
    ? dayjs(message.created_at).format('MMM D, YYYY')
    : 'recently'
  const introSource = senderFirstName ? `${senderFirstName} from ${floristName}` : floristName

  return {
    subject: `Re: Your inquiry to ${floristName}`,
    emailBody: [
      `Hi ${customerFirstName},`,
      '',
      `This is ${introSource} following up on the inquiry you submitted on ${submittedOn}.`,
      '',
      "I'd be happy to answer any questions and talk through next steps.",
      '',
      'Best,',
      senderFirstName || floristName,
      senderFirstName ? floristName : null,
    ]
      .filter((line) => line != null)
      .join('\n'),
    textBody: `Hi ${customerFirstName}, this is ${introSource} following up on the inquiry you submitted on ${submittedOn}.`,
  }
}

function buildMailtoHref(email, subject, body) {
  if (!email) {
    return '#'
  }

  const params = [
    ['subject', subject],
    ['body', body],
  ]
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

  return `mailto:${email}?${params}`
}

function getCreateCustomerDraftFromMessage(message) {
  return {
    name: message?.name || '',
    email: message?.email || '',
    phone: message?.phone || '',
  }
}

function messageHasContact(message) {
  return Boolean(message?.email?.trim()) || Boolean(message?.phone?.trim())
}

function getMessagePageTitle(message, messageId) {
  if (message?.name) {
    return `Message from ${message.name}`
  }

  return `Message #${messageId}`
}

async function createAndLinkCustomerFromMessage({
  message,
  createMessageCustomerMutation,
  draft = getCreateCustomerDraftFromMessage(message),
}) {
  const linkedMessage = await createMessageCustomerMutation.mutateAsync({
    messageId: message.id,
    ...draft,
    source_type: CustomerSourceType.Admin.Value,
  })

  const createdCustomerId = linkedMessage?.customer?.id ?? linkedMessage?.customer_id
  if (!createdCustomerId) {
    throw new Error('Customer was linked without an id.')
  }

  return createdCustomerId
}

function MessageReplyMenu({ message, organizationName, userName }) {
  const hasEmail = Boolean(message?.email)
  const normalizedPhone = normalizePhoneHref(message?.phone)
  const hasPhone = Boolean(normalizedPhone)
  const replyDraft = buildReplyDraft({ message, organizationName, userName })

  const handleTextClick = async () => {
    if (!replyDraft.textBody || !globalThis.navigator?.clipboard?.writeText) {
      if (normalizedPhone && typeof globalThis.open === 'function') {
        globalThis.open(`sms:${normalizedPhone}`, '_self')
      }

      return
    }

    try {
      await globalThis.navigator.clipboard.writeText(replyDraft.textBody)
      toast.success('Suggested text copied to clipboard')
    } catch {
      // Ignore clipboard errors so the sms link can still open.
    }

    if (normalizedPhone && typeof globalThis.open === 'function') {
      globalThis.open(`sms:${normalizedPhone}`, '_self')
    }
  }

  if (!hasEmail && !hasPhone) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <AdminHeaderActionButton variant="outline">
          <Reply className="size-4" />
          Reply
          <ChevronDown className="size-4" />
        </AdminHeaderActionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" size="fit">
        {hasEmail ? (
          <DropdownMenuItem asChild>
            <a href={buildMailtoHref(message.email, replyDraft.subject, replyDraft.emailBody)}>
              <Mail className="size-4" />
              Email customer
            </a>
          </DropdownMenuItem>
        ) : null}
        {hasPhone ? (
          <DropdownMenuItem asChild>
            <a href={`tel:${normalizedPhone}`}>
              <Phone className="size-4" />
              Call customer
            </a>
          </DropdownMenuItem>
        ) : null}
        {hasPhone ? (
          <DropdownMenuItem onClick={handleTextClick}>
            <MessageSquareText className="size-4" />
            Text customer
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MessageContactField({ label, value, href }) {
  const displayValue = typeof value === 'string' ? value.trim() : value
  const hasValue = Boolean(displayValue)
  const valueClassName = 'mt-1 block break-words text-sm font-semibold text-foreground'

  return (
    <div className="min-w-0">
      <Typography variant="label" tone="muted" className="block">
        {label}
      </Typography>
      {href && hasValue ? (
        <a href={href} className={cn(valueClassName, 'hover:underline')}>
          {displayValue}
        </a>
      ) : (
        <p className={valueClassName}>
          {hasValue ? (
            displayValue
          ) : (
            <span className="font-normal text-muted-foreground">None</span>
          )}
        </p>
      )}
    </div>
  )
}

function getMessageContactFormValues(message) {
  return {
    name: message?.name || '',
    email: message?.email || '',
    phone: message?.phone || '',
  }
}

function MessageContactInformationPanel({ message, isReadOnly }) {
  const { formatPhone } = usePhoneFormatter()
  const phoneHref = normalizePhoneHref(message?.phone)
  const updateMessageMutation = useUpdateMessageMutation()
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const form = useAppForm({
    defaultValues: getMessageContactFormValues(message),
    validators: {
      onSubmit: messageContactInformationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          const response = await updateMessageMutation.mutateAsync({
            messageId: message.id,
            ...value,
          })

          formApi.reset(getMessageContactFormValues(response || { ...message, ...value }))
          setIsEditorOpen(false)
          toast.success('Contact information updated')
        } catch (error) {
          toast.error('Failed to update contact information', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const openEditor = () => {
    form.reset(getMessageContactFormValues(message))
    setIsEditorOpen(true)
  }

  const closeEditor = () => {
    form.reset(getMessageContactFormValues(message))
    setIsEditorOpen(false)
  }

  if (!message?.id) {
    return null
  }

  return (
    <>
      <DetailCard accent="periwinkle" contentClassName="space-y-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <DetailIconBadge icon={Phone} tone="periwinkle" />
            <div className="min-w-0">
              <Typography variant="cardTitle">Contact Information</Typography>
            </div>
          </div>
          {!isReadOnly ? (
            <Can do="edit" on="messages">
              <IconActionButton
                icon={Pencil}
                label="Edit contact information"
                className={detailSurfaceActionClass}
                onClick={openEditor}
              />
            </Can>
          ) : null}
        </div>
        <DetailWell compact className="space-y-4">
          <MessageContactField label="Name" value={message?.name} />
          <MessageContactField
            label="Email"
            value={message?.email}
            href={message?.email ? `mailto:${message.email}` : null}
          />
          <MessageContactField
            label="Phone"
            value={formatPhone(message?.phone)}
            href={phoneHref ? `tel:${phoneHref}` : null}
          />
        </DetailWell>
      </DetailCard>

      <Sheet
        open={isEditorOpen}
        onOpenChange={(open) => {
          if (open) {
            openEditor()
          } else {
            closeEditor()
          }
        }}
      >
        <SheetContent className="w-full p-0 sm:max-w-md">
          <form.AppForm aria-label="Message Contact Information Form">
            <form.Form className="flex min-h-0 flex-1 flex-col">
              <SheetHeader className="border-b border-border/70 p-6 pr-12">
                <div className="flex items-center gap-3">
                  <DetailIconBadge icon={Phone} tone="periwinkle" />
                  <div className="min-w-0">
                    <SheetTitle>Contact Information</SheetTitle>
                    <SheetDescription>Update who sent this message.</SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="space-y-5 p-6">
                <form.FormError />
                <form.AppField name="name">
                  {(field) => <field.TextInput label="Name" disabled={isReadOnly} />}
                </form.AppField>
                <form.AppField name="email">
                  {(field) => <field.EmailInput label="Email" disabled={isReadOnly} />}
                </form.AppField>
                <form.AppField name="phone">
                  {(field) => <field.PhoneInput label="Phone" disabled={isReadOnly} />}
                </form.AppField>
              </div>
              {!isReadOnly ? (
                <Can do="edit" on="messages">
                  <SheetFooter className="flex-row justify-end border-t border-border/70 p-4">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={updateMessageMutation.isPending}
                      onClick={closeEditor}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateMessageMutation.isPending}>
                      {updateMessageMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                  </SheetFooter>
                </Can>
              ) : null}
            </form.Form>
          </form.AppForm>
        </SheetContent>
      </Sheet>
    </>
  )
}

function renderCustomerSearchOption(customer) {
  const contactLine = [customer.email, customer.phone].filter(Boolean).join(' • ')

  return (
    <div className="flex min-w-0 items-start justify-between gap-3 py-1">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">
          {customer.name || `Customer #${customer.id}`}
        </p>
        <Typography variant="supporting" className="mt-1 block truncate">
          {contactLine || 'No contact info'}
        </Typography>
      </div>
      <Link2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    </div>
  )
}

function MessageCustomerLinkPanel({ message, isReadOnly, organizationId }) {
  const path = usePathHelper()
  const linkMessageCustomerMutation = useLinkMessageCustomerMutation()
  const createMessageCustomerMutation = useCreateMessageCustomerMutation()
  const linkedCustomer = message?.customer || null
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState(() => getCreateCustomerDraftFromMessage(message))

  const resetCreateDraft = () => {
    setCreateDraft(getCreateCustomerDraftFromMessage(message))
  }

  const fetchCustomerOptions = (searchInput) =>
    getCustomers({
      queryKey: [
        {
          organizationId,
          filters: { search: searchInput },
        },
      ],
    })

  const handleCustomerLink = (customerId, successMessage) => {
    linkMessageCustomerMutation.mutate(
      {
        messageId: message.id,
        customerId,
      },
      {
        onSuccess: () => {
          setSearchTerm('')
          toast.success(successMessage)
        },
        onError: (error) =>
          toast.error('Failed to update customer link', {
            description: getErrorMessage(error),
          }),
      }
    )
  }

  const handleUnlinkCustomer = () => {
    handleCustomerLink(null, 'Customer link removed')
  }

  const handleCreateCustomer = async () => {
    try {
      await createAndLinkCustomerFromMessage({
        message,
        createMessageCustomerMutation,
        draft: createDraft,
      })

      setIsCreateDialogOpen(false)
      setSearchTerm('')
      toast.success('Customer created and linked')
    } catch (error) {
      toast.error('Failed to create customer', {
        description: getErrorMessage(error),
      })
    }
  }

  const openCreateCustomerDialog = () => {
    resetCreateDraft()
    setIsCreateDialogOpen(true)
  }

  const isCustomerMutationPending =
    linkMessageCustomerMutation.isPending || createMessageCustomerMutation.isPending
  const canCreateCustomerFromMessage = messageHasContact(createDraft)

  if (!message?.id) {
    return null
  }

  return (
    <>
      {linkedCustomer?.id ? (
        <DetailCard accent="mint" contentClassName="space-y-4 p-5">
          <div className="flex items-start gap-3">
            <DetailIconBadge icon={Link2} tone="mint" />
            <div className="min-w-0">
              <Typography variant="eyebrow">Linked Customer</Typography>
            </div>
          </div>
          <DetailWell compact>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Link {...path.customers.show(linkedCustomer.id).link}>
                  <Typography variant="labelStrong" interaction="hoverUnderline">
                    {linkedCustomer.name}
                  </Typography>
                </Link>
                <Typography variant="supporting" className="mt-1 block">
                  {linkedCustomer.email || linkedCustomer.phone}
                </Typography>
              </div>
              {!isReadOnly ? (
                <Can do="edit" on="messages">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isCustomerMutationPending}
                    onClick={handleUnlinkCustomer}
                  >
                    <Unlink className="size-4" />
                    Unlink
                  </Button>
                </Can>
              ) : null}
            </div>
          </DetailWell>
        </DetailCard>
      ) : (
        <DetailCard accent="neutral" contentClassName="space-y-4 p-5">
          <div className="flex items-start gap-3">
            <DetailIconBadge icon={Link2} tone="neutral" />
            <div className="space-y-1">
              <Typography variant="eyebrow">Customer</Typography>
              <Typography variant="h5">No linked customer</Typography>
            </div>
          </div>
          {!isReadOnly ? (
            <Can do="edit" on="messages">
              <div className="space-y-4">
                <Can do="list" on="customers">
                  <DetailWell compact className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm font-semibold">Merge with known customer</p>
                        <Typography variant="supporting" className="block">
                          Search for an existing customer to link to this message.
                        </Typography>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <AsyncAutocompleteInput
                        id="message-customer-search"
                        label="Search Customers"
                        value={searchTerm}
                        placeholder="Search by name, email, or phone"
                        minChars={2}
                        dropdownInPortal
                        fetchOptions={fetchCustomerOptions}
                        renderOption={renderCustomerSearchOption}
                        disabled={isCustomerMutationPending}
                        onChange={setSearchTerm}
                        onOptionSelected={(customer) => {
                          if (isCustomerMutationPending) {
                            return
                          }

                          handleCustomerLink(customer.id, 'Customer linked')
                        }}
                      />
                    </div>
                  </DetailWell>
                </Can>
                <Can do="create" on="customers">
                  <DetailWell compact className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Create a new customer from this lead</p>
                      <Typography variant="supporting" className="mt-1 block">
                        Create the new customer and auto-link them to this message.
                      </Typography>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openCreateCustomerDialog}
                        disabled={isCustomerMutationPending}
                        className="mt-3 inline-flex items-center gap-2"
                      >
                        <UserPlus className="size-4" />
                        Create Customer
                      </Button>
                    </div>
                  </DetailWell>
                </Can>
              </div>
            </Can>
          ) : null}
        </DetailCard>
      )}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Customer</DialogTitle>
            <DialogDescription>
              Create a customer from this message and link them right away.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="message-create-customer-name">Name</Label>
              <Input
                id="message-create-customer-name"
                value={createDraft.name}
                onChange={(event) =>
                  setCreateDraft((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Customer name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message-create-customer-phone">Phone</Label>
              <Input
                id="message-create-customer-phone"
                value={createDraft.phone}
                onChange={(event) =>
                  setCreateDraft((current) => ({ ...current, phone: event.target.value }))
                }
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message-create-customer-email">Email</Label>
              <Input
                id="message-create-customer-email"
                type="email"
                value={createDraft.email}
                onChange={(event) =>
                  setCreateDraft((current) => ({
                    ...current,
                    email: normalizeEmailInput(event.target.value),
                  }))
                }
                placeholder="me@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isCustomerMutationPending}
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canCreateCustomerFromMessage || isCustomerMutationPending}
              onClick={handleCreateCustomer}
            >
              <Plus className="size-4" />
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function MessageCreateSalesOrderButton({ message, isReadOnly }) {
  const path = usePathHelper()
  const navigate = useNavigate()
  const ability = useAbility()
  const createMessageCustomerMutation = useCreateMessageCustomerMutation()
  const createPosCartMutation = useCreateCustomerPosCartMutation()
  const linkedCustomerId = message?.customer?.id
  const canCreateForLinkedCustomer = Boolean(linkedCustomerId)
  const canCreateFromUnlinkedMessage =
    !isReadOnly && ability.can('create', 'customers') && messageHasContact(message)
  const canCreateSalesOrder =
    message?.id &&
    ability.can('create', 'sales-orders') &&
    (canCreateForLinkedCustomer || canCreateFromUnlinkedMessage)
  const isPending = createMessageCustomerMutation.isPending || createPosCartMutation.isPending

  const handleCreateSalesOrder = async () => {
    try {
      const customerId =
        linkedCustomerId ||
        (await createAndLinkCustomerFromMessage({
          message,
          createMessageCustomerMutation,
        }))
      const response = await createPosCartMutation.mutateAsync(customerId)

      toast.success(linkedCustomerId ? 'POS cart created' : 'Customer linked and POS cart created')
      await navigate({
        ...path.salesOrders.create().search({ cartId: response?.cart_id ?? response?.id }).link,
      })
    } catch (error) {
      toast.error('Failed to create order', {
        description: getErrorMessage(error),
      })
    }
  }

  if (!canCreateSalesOrder) {
    return null
  }

  return (
    <AdminHeaderActionButton type="button" disabled={isPending} onClick={handleCreateSalesOrder}>
      <ShoppingCart className="size-4" />
      Create Order
    </AdminHeaderActionButton>
  )
}

function MessageMoreActionsMenu({ message, isReadOnly }) {
  const path = usePathHelper()
  const navigate = useNavigate()
  const deleteMessageMutation = useSoftDeleteMessageMutation({ cleanupOnSuccess: false })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true)
  }

  const deleteMessage = async () => {
    try {
      await deleteMessageMutation.mutateAsync(message.id)
      toast.success('Message deleted successfully')
      await navigate({ ...path.messages.index().link, ignoreBlocker: true, replace: true })
      void Promise.resolve()
        .then(() => deleteMessageMutation.cleanupDeletedMessage?.(message.id))
        .catch(() => undefined)
    } catch (error) {
      toast.error('Failed to delete message', {
        description: getErrorMessage(error),
      })
    }
  }

  if (isReadOnly || !message?.id) {
    return null
  }

  return (
    <Can do="delete" on="messages">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderIconButton icon={EllipsisVertical} label="More message actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="sm">
          <DropdownMenuItem variant="destructive" onSelect={openDeleteDialog}>
            <Trash2 className="size-4" />
            Delete message
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmationDialog
        trigger={null}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        variant="destructive"
        title="Delete Message"
        description="Are you sure you want to delete this message?"
        confirmText="Delete"
        pendingText="Deleting..."
        isPending={deleteMessageMutation.isPending}
        onConfirm={deleteMessage}
      />
    </Can>
  )
}

function MessageActivityTabs({ message, isReadOnly }) {
  const [activeTab, setActiveTab] = useState('notes')

  return (
    <div className="space-y-3">
      <div className="px-1">
        <Typography variant="h4" className="pb-0">
          Message Activity
        </Typography>
      </div>
      <DetailWorkspaceTabs value={activeTab} onValueChange={setActiveTab}>
        <div className="min-w-0">
          <DetailWorkspaceTabsList>
            <DetailWorkspaceTab value="notes" icon={NotebookPen}>
              Notes
            </DetailWorkspaceTab>
          </DetailWorkspaceTabsList>
        </div>
        <DetailWorkspacePanel value="notes" forceMount>
          <MessageInternalNotesPanel message={message} isReadOnly={isReadOnly} embedded />
        </DetailWorkspacePanel>
      </DetailWorkspaceTabs>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/messages/$messageId')({
  component: MessageRouteComponent,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => messageIdSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'messages')) throw notFound()
    const message = await context.queryClient.ensureQueryData(
      messageQueries.show({
        organizationId: params.orgId,
        messageId: params.messageId,
      })
    )
    return { message, isReadOnly: !context.ability.can('edit', 'messages') }
  },
})

export function MessageRouteComponent() {
  const { messageId, orgId } = Route.useParams()
  const { isReadOnly } = Route.useLoaderData()
  const ability = useAbility()
  const { organization, user } = useOrganizationContext()
  const { data: message } = useMessage(messageId, { organizationId: orgId })
  const markMessageReadMutation = useMarkMessageReadMutation()
  const markMessageUnreadMutation = useMarkMessageUnreadMutation()
  const autoReadMessageIdRef = useRef(null)
  const suppressAutoReadMessageIdRef = useRef(null)
  const canSnoopUnobtrusively = ability.can('access', 'global')
  const markMessageRead = useEffectEvent((targetMessageId) => {
    markMessageReadMutation.mutate(targetMessageId)
  })

  useEffect(() => {
    autoReadMessageIdRef.current = null
    suppressAutoReadMessageIdRef.current = null
  }, [messageId])

  useEffect(() => {
    if (
      !message?.id ||
      canSnoopUnobtrusively ||
      message.read_at ||
      autoReadMessageIdRef.current === message.id ||
      suppressAutoReadMessageIdRef.current === message.id
    ) {
      return
    }

    autoReadMessageIdRef.current = message.id
    markMessageRead(message.id)
  }, [canSnoopUnobtrusively, message?.id, message?.read_at])

  const handleMarkMessageRead = () => {
    if (!message?.id) {
      return
    }

    markMessageReadMutation.mutate(message.id, {
      onError: (error) => {
        toast.error('Failed to mark message as read', {
          description: getErrorMessage(error),
        })
      },
    })
  }

  const handleMarkMessageUnread = async () => {
    if (!message?.id) {
      return
    }

    try {
      suppressAutoReadMessageIdRef.current = message.id
      await markMessageUnreadMutation.mutateAsync(message.id)
    } catch (error) {
      suppressAutoReadMessageIdRef.current = null
      toast.error('Failed to mark message as unread', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="-mr-4 mb-10 pr-6 pt-1">
      <AdminDetailGrid
        main={
          <>
            <AdminRecordHeader
              icon={MessageSquareText}
              iconTone="periwinkle"
              title={getMessagePageTitle(message, messageId)}
              subtitle={getMessageSourceSummary(message)}
              actions={
                <>
                  <MessageCreateSalesOrderButton message={message} isReadOnly={isReadOnly} />
                  {!isReadOnly ? (
                    <Can do="edit" on="messages">
                      {message?.read_at && !markMessageReadMutation.isPending ? (
                        <AdminHeaderActionButton
                          type="button"
                          variant="outline"
                          disabled={markMessageUnreadMutation.isPending}
                          onClick={handleMarkMessageUnread}
                        >
                          <MailOpen className={cn(detailPaleIconClass, 'text-current')} />
                          {markMessageUnreadMutation.isPending
                            ? 'Marking unread...'
                            : 'Mark as unread'}
                        </AdminHeaderActionButton>
                      ) : !message?.read_at && !markMessageUnreadMutation.isPending ? (
                        <AdminHeaderActionButton
                          type="button"
                          variant="outline"
                          disabled={markMessageReadMutation.isPending}
                          onClick={handleMarkMessageRead}
                        >
                          <MailCheck className={cn(detailPaleIconClass, 'text-current')} />
                          {markMessageReadMutation.isPending ? 'Marking read...' : 'Mark as read'}
                        </AdminHeaderActionButton>
                      ) : null}
                    </Can>
                  ) : null}
                  <MessageReplyMenu
                    message={message}
                    organizationName={organization?.name}
                    userName={user?.name}
                  />
                  <MessageMoreActionsMenu message={message} isReadOnly={isReadOnly} />
                </>
              }
            />
            <MessageForm initialData={message} />
            <MessageActivityTabs message={message} isReadOnly={isReadOnly} />
          </>
        }
        aside={
          <>
            <MessageContactInformationPanel message={message} isReadOnly={isReadOnly} />
            <MessageCustomerLinkPanel
              message={message}
              isReadOnly={isReadOnly}
              organizationId={orgId}
            />
          </>
        }
      />
    </div>
  )
}
